import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { useFormik, useFormikContext } from 'formik';
import { PracticeDetailVM } from 'practice/view-models/practice-detail';
import { Fragment, memo, useEffect, useRef, useState } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import * as yup from 'yup';
import AccordionPrintComunicationForm, { PrintComunicationVM } from './form';
import ModalPrintSuccess from './modal';
import { useParams } from 'react-router-dom';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { toast } from 'react-toastify';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import { regexAt, regexParentesi, regexSlash } from 'shell/constants/regex';
import { TypeInitiative } from 'practice/constants/practice-management-step';

interface PrintComunicationProps {
  loadUpdateDate: () => Promise<PracticeDetailVM> | void;
  tipoIniziativa?: string;
}

const AccordionPrintComunicationComponent = ({
  loadUpdateDate,
  tipoIniziativa,
}: PrintComunicationProps): React.ReactElement => {
  const [open, setOpen] = useState(true);

  const [openModal, setOpenModal] = useState(false);

  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });

  const params = useParams();

  const ref = useRef<HTMLDivElement | null>(null);

  const contextPage = useFormikContext<PracticeDetailVM>();

  const printComunication = useHttpClient<Blob>(
    `${__PRACTICE_API_URL__}/pratiche/stampa/comunicazione-ostativa`,
    {
      options: {
        method: 'POST',
      },
    }
  );

  const registerCommunication = useHttpClient(`${__PRACTICE_API_URL__}/comunicazioni/ostativa`, {
    options: {
      method: 'POST',
    },
  });

  useEffect(() => {
    ref.current?.scrollIntoView();
  }, []);

  const formContext = useFormik<PrintComunicationVM>({
    initialValues: {
      typeInitiative: tipoIniziativa,
      reason:
        "Dall'accertamento effettuato, a mezzo del messo comunale, non risulta la dimora abituale all'indirizzo indicato, quale condizione essenziale stabilita dalla legge per la residenza anagrafica (art. 3, D.P.R. 30 Maggio 1989 n. 223-Regolamento Anagrafico).",
      note: '',
    },
    validationSchema: yup
      .object()
      .shape({
        reason: yup.string().nullable().required('Il campo è obbligatorio'),
        note: yup
          .string()
          .nullable()
          .when('typeInitiative', (typeInitiative, formvalidationSchema) =>
            typeInitiative === TypeInitiative.Anpr
              ? formvalidationSchema.required('Il campo è obbligatorio')
              : formvalidationSchema.nullable()
          ),
      })
      .test('', '', (values) => {
        if (!values.note) {
          return true;
        }
        // const regularExpDiacritic = new RegExp(onlyDiacriticRegex);

        const regularExpParentesi = new RegExp(regexParentesi);

        const regularExpSlash = new RegExp(regexSlash);

        const regularExpAt = new RegExp(regexAt);

        if (
          regularExpParentesi.test(values.note) ||
          regularExpSlash.test(values.note) ||
          regularExpAt.test(values.note)
        ) {
          return new yup.ValidationError(
            'Il campo non deve contenere parentesi, slash e chiocciola',
            undefined,
            'note'
          );
        }

        return true;
      }),
    onSubmit: (values) => {
      (
        printComunication({
          data: {
            key: {
              codiceEnte: params?.codiceEnte,
              tipoRegistro: params?.tipoRegistro,
              idAnnoPratica: params?.idAnnoPratica
                ? Number.parseInt(params?.idAnnoPratica, 10)
                : undefined,
              progressivoPratica: params?.progressivoPratica
                ? Number.parseInt(params?.progressivoPratica, 10)
                : undefined,
            },
            motivo: values.reason || undefined,
            note: values.note || undefined,
          },
          responseType: 'blob',
        }) as ResponsePromise<Blob>
      ).then((response) => {
        toast.success('Operazione eseguita con successo');
        const pdfCertificate = response?.data;
        if (pdfCertificate) {
          const blob = new Blob([pdfCertificate], { type: 'application/pdf' });
          blobToBase64(blob).then((base64) => {
            setPdfModal({
              data: base64.split(',')[1],
              name: 'Stampa Comunicazione Ostativa (Art. 10 Bis)',
              open: true,
            });
          });
        }
        contextPage.setFieldValue('notePrintOstativa', values.note);
      });
    },
  });

  const onYesModalPrint = useEventCallback(() => {
    (
      registerCommunication({
        data: {
          codiceEnte: params?.codiceEnte,
          tipoRegistro: params?.tipoRegistro,
          idAnnoPratica: params?.idAnnoPratica
            ? Number.parseInt(params?.idAnnoPratica, 10)
            : undefined,
          progressivoPratica: params?.progressivoPratica
            ? Number.parseInt(params?.progressivoPratica, 10)
            : undefined,
          descrizioneComunicazione: formContext.values.reason || undefined,
          note: formContext.values.note || undefined,
        },
      }) as ResponsePromise<void>
    ).then(async () => {
      toast.success('Operazione eseguita con successo');
      setOpenModal(false);

      // aggiornamento stato pratica
      contextPage.setFieldValue('practice', {
        ...(contextPage.values.practice || {}),
        codiceStatoPratica: '5',
        tipoOperazione: '27',
      });

      // se cambia lo stato della pratica ricaricare dati
      const newData: PracticeDetailVM | void = await loadUpdateDate();

      if (newData) {
        contextPage.setValues({
          ...(contextPage.values || {}),
          ...newData,
        });
      }
    });
  });

  return (
    <Fragment>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
        ref={ref}
      >
        <AccordionSummary
          aria-controls="panel-end-practice-content"
          id="panel-end-practice-header"
          expandIcon={<ExpandMore />}
        >
          <Typography>Stampa Comunicazione Ostativa (Art. 10 Bis)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AccordionPrintComunicationForm
            values={formContext.values}
            onChange={formContext.setFieldValue}
            errors={formContext.errors}
            touched={formContext.touched}
          />
          <div className="pmi-align-right pmi-pt-2">
            <PmiButton
              color="cdmBrandThird"
              variant="outlined"
              onClick={() => formContext.handleSubmit()}
              id="print"
            >
              Stampa
            </PmiButton>
          </div>
        </AccordionDetails>
      </Accordion>
      <ModalViewerPDF
        open={pdfModal.open}
        data={pdfModal.data}
        name={pdfModal.name}
        title="Stampa Comunicazione Ostativa (Art. 10 Bis)"
        onClose={() => {
          setPdfModal({ data: '', open: false, name: '' });
          setOpenModal(true);
        }}
      />
      <ModalPrintSuccess
        open={openModal}
        onClose={() => setOpenModal(false)}
        onYes={onYesModalPrint}
      />
    </Fragment>
  );
};

AccordionPrintComunicationComponent.displayName = 'Accordion Print comunication component';

const AccordionPrintComunication = memo(AccordionPrintComunicationComponent);

export default AccordionPrintComunication;
